import { useEffect, useState } from 'react';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import { useSearchParams } from 'react-router-dom';
import { useSearchVariable } from 'hooks/search';
import { FilterProps } from './LargeTable';
import _ from 'lodash';

type SortingColumnTitleProps = {
  column_key: string
  column_name: string
  filterProps: FilterProps
};

type SortStatus = "ASCENDING" | "DESCENDING" | "OFF"

export const SortingColumnTitle = ({ column_key, column_name, filterProps }: SortingColumnTitleProps) => {
  const { filter, setFilter } = filterProps
  const [sortStatus, setSortStatus] = useState<SortStatus>("OFF");

  useEffect(() => {
    if (filter.sort_setting?.column != column_key) setSortStatus("OFF")
  }, [filter]);

  function toggleFilter(): void {
    switch (sortStatus) {
      case 'ASCENDING': applySort('DESCENDING'); break;
      case 'DESCENDING': applySort('OFF'); break;
      case 'OFF': applySort('ASCENDING'); break;
    }
  }

  function applySort(sort: SortStatus) {
    setSortStatus(sort)
    setFilter(curFilter => {
      const newFilter = _.cloneDeep(curFilter)
      if (sort == 'OFF') {
        newFilter.sort_setting = undefined
        return newFilter
      } else {
        const direction = sort == 'ASCENDING' ? 'ASCENDING' : 'DESCENDING'
        newFilter.sort_setting = { column: column_key, direction }
        return newFilter
      }
    })
  }

  const icon = (() => {
    switch (sortStatus) {
      case 'ASCENDING': return <SouthIcon fontSize='small'/>
      case 'DESCENDING': return <NorthIcon fontSize='small'/>
      default: return null
    }
  })()

  return (
    <button
      type="button"
      onClick={toggleFilter}
      style={{
        all: "unset",
        cursor: "pointer",
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '10px'
      }}
    >
      {column_name} {icon}
    </button>
  );
}

export const UrlSortingColumnTitle = ({ column_key, column_name }: SortingColumnTitleProps) => {
  const [_, setSearchParams] = useSearchParams()
  const sortSetting = useSearchVariable('sort')
  const [sortStatus, setSortStatus] = useState<SortStatus>("OFF");

  function updateURLSort(value : string) {
    setSearchParams(params => {
      params.set('sort', encodeURIComponent(value))
      return params
    })
  }
  function removeURLSort() {
    setSearchParams(params => {
      params.delete('sort')
      return params
    })
  }

  function hasURLSort() : boolean {
    return sortSetting?.endsWith(column_key) ?? false
  }

  function toggleFilter(): void {
    switch (sortStatus) {
      case 'ASCENDING': updateURLSort("des_" + column_key); break;
      case 'DESCENDING': removeURLSort(); break;
      case 'OFF': updateURLSort("asc_" + column_key); break;
    }
  }

  function deriveStatusFromString(sort_setting : string): SortStatus {
    if (sort_setting == "asc_" + column_key) return "ASCENDING"
    else if (sort_setting == "des_" + column_key) return "DESCENDING"
    else return "OFF"
  }

  useEffect(() => {
    if (hasURLSort()) {
      setSortStatus(deriveStatusFromString(sortSetting!));
    } else {
      setSortStatus("OFF")
    }
  }, [sortSetting]);

  const icon = (() => {
    switch (sortStatus) {
      case 'ASCENDING': return <SouthIcon fontSize='small'/>
      case 'DESCENDING': return <NorthIcon fontSize='small'/>
      default: return null
    }
  })()

  return (
    <button
      type="button"
      onClick={toggleFilter}
      style={{
        all: "unset",
        cursor: "pointer",
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '10px'
      }}
    >
      {column_name} {icon}
    </button>
  );
}
